import { createContext, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  checkAuth,
  getUserDetails,
  updateUserDetails,
  changePassword,
  logout,
  checkEmailVerificationStatus,
  resendVerificationEmail,
  deleteAccount,
  getTransactions,
  downloadTransactionReport,
  addAddress,
  getAddresses,
  updateAddress,
  deleteAddress,
  subscribeToNewsletter,
  setUser,
  selectUser,
  selectAuthLoading,
} from "../store/slices/authSlice";
import {
  createTicket,
  getUserTickets,
  getTicketById,
  getTicketUpdates,
  replyToTicket,
  deleteTicket,
} from "../store/slices/ticketSlice";
import useTokenRefresh from "../hooks/useTokenRefresh";
import { setSentryUser, clearSentryUser } from "../analytics/sentry";

const AuthContext = createContext();

// Routes that require authentication check
// Only routes that actually need auth verification should be listed here
const routesRequiringAuth = [
  "/dashboard", // User dashboard routes
  "/cart",      // Cart page - needs auth to show correct checkout button
  "/checkout",  // Checkout page - requires authentication
];

// Check if current route requires authentication
const shouldCheckAuth = (pathname) => {
  return routesRequiringAuth.some((route) => pathname.startsWith(route));
};

export const AuthProvider = ({ children }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const loading = useAppSelector(selectAuthLoading);
  const location = useLocation();
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  useTokenRefresh(user);

  const fetchUser = async () => {
    // Cancel any previous request immediately to prevent duplicate requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Increment request ID to track the latest request
    const currentRequestId = ++requestIdRef.current;

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Use Redux thunk for auth check
      await dispatch(checkAuth());
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    } catch (error) {
      // Ignore cancellation errors
      const isCanceled = 
        error.name === "CanceledError" || 
        error.name === "AbortError" ||
        error.code === "ERR_CANCELED" ||
        error.message === "canceled" ||
        abortController.signal.aborted;

      if (isCanceled) {
        return;
      }

      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  // ============================= AUTH FUNCTIONS =============================
  // Wrapper functions that use Redux thunks but maintain the same API

  const getUserDetailsWrapper = async () => {
    try {
      const result = await dispatch(getUserDetails()).unwrap();
      return result;
    } catch (error) {
      return null;
    }
  };

  const updateUserDetailsWrapper = async (data) => {
    try {
      const result = await dispatch(updateUserDetails(data)).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const changePasswordWrapper = async (oldPassword, newPassword) => {
    try {
      const result = await dispatch(changePassword({ oldPassword, newPassword })).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const logoutWrapper = async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (err) {
      // Error already handled in thunk
    }
  };

  const checkEmailVerificationStatusWrapper = async () => {
    try {
      const result = await dispatch(checkEmailVerificationStatus()).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const resendVerificationEmailWrapper = async () => {
    try {
      const result = await dispatch(resendVerificationEmail()).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const deleteAccountWrapper = async () => {
    try {
      await dispatch(deleteAccount()).unwrap();
    } catch (error) {
      throw error;
    }
  };

  // ============================= TRANSACTIONS =============================

  const getTransactionsWrapper = async () => {
    try {
      const result = await dispatch(getTransactions()).unwrap();
      return result;
    } catch (error) {
      return [];
    }
  };

  const downloadTransactionReportWrapper = async () => {
    try {
      await dispatch(downloadTransactionReport()).unwrap();
    } catch (error) {
      // Error already handled in thunk
    }
  };

  // ============================= ADDRESSES =============================

  const addAddressWrapper = async (data) => {
    try {
      const result = await dispatch(addAddress(data)).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const getAddressesWrapper = async () => {
    try {
      const result = await dispatch(getAddresses()).unwrap();
      return result;
    } catch (error) {
      return [];
    }
  };

  const updateAddressWrapper = async (id, data) => {
    try {
      const result = await dispatch(updateAddress({ id, data })).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const deleteAddressWrapper = async (id) => {
    try {
      await dispatch(deleteAddress(id)).unwrap();
    } catch (error) {
      throw error;
    }
  };

  // ============================= NEWSLETTER =============================

  const subscribeToNewsletterWrapper = async (email, name = "") => {
    try {
      const result = await dispatch(subscribeToNewsletter({ email, name })).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  // ============================= CART SYNC =============================
  // Note: Cart sync is now handled in cartSlice, but keeping this for backward compatibility
  const syncGuestCartToServer = async () => {
    // This is now handled by Redux cart slice, but we keep the function for backward compatibility
    // Components should use Redux syncCartToServer directly
    await dispatch(syncCartToServer());
  };

  // ============================= TICKETS =============================

  const createTicketWrapper = async (ticketData) => {
    try {
      const result = await dispatch(createTicket(ticketData)).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const getUserTicketsWrapper = async (page = 1, limit = 10) => {
    try {
      const result = await dispatch(getUserTickets({ page, limit })).unwrap();
      return result;
    } catch (error) {
      return { tickets: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 0, hasMore: false } };
    }
  };

  const getTicketByIdWrapper = async (ticketId) => {
    try {
      const result = await dispatch(getTicketById(ticketId)).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const getTicketUpdatesWrapper = async (ticketId) => {
    try {
      const result = await dispatch(getTicketUpdates(ticketId)).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  const replyToTicketWrapper = async (ticketId, message, parentMessageId = null) => {
    try {
      const result = await dispatch(replyToTicket({ ticketId, message, parentMessageId })).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Keep updateTicket for backward compatibility
  const updateTicket = async (ticketId, updateData) => {
    if (updateData.message) {
      return replyToTicketWrapper(ticketId, updateData.message, updateData.parent_message_id || null);
    }
    throw new Error("updateTicket now requires a message. Use replyToTicket instead.");
  };

  const deleteTicketWrapper = async (ticketId) => {
    try {
      const result = await dispatch(deleteTicket(ticketId)).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  // ============================= SENTRY USER TRACKING =============================

  // Update Sentry user context whenever user state changes
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id || user._id,
        email: user.email,
        username: user.username || user.name || user.firstName,
      });
    } else {
      clearSentryUser();
    }
  }, [user]);

  // ============================= INIT =============================

  useEffect(() => {
    // Only fetch user on routes that require authentication
    if (shouldCheckAuth(location.pathname)) {
      fetchUser();
    }
    // For public routes, we don't need to do anything - loading state is managed by Redux
    // The loading will be false by default and only true when checkAuth is pending

    // Cleanup: cancel any pending request when pathname changes or component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // setUser wrapper that uses Redux
  const setUserWrapper = (userData) => {
    dispatch(setUser(userData));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser: setUserWrapper,
        logout: logoutWrapper,
        loading,
        fetchUser,
        getUserDetails: getUserDetailsWrapper,
        updateUserDetails: updateUserDetailsWrapper,
        changePassword: changePasswordWrapper,
        checkEmailVerificationStatus: checkEmailVerificationStatusWrapper,
        resendVerificationEmail: resendVerificationEmailWrapper,
        deleteAccount: deleteAccountWrapper,
        getTransactions: getTransactionsWrapper,
        downloadTransactionReport: downloadTransactionReportWrapper,
        addAddress: addAddressWrapper,
        getAddresses: getAddressesWrapper,
        updateAddress: updateAddressWrapper,
        deleteAddress: deleteAddressWrapper,
        subscribeToNewsletter: subscribeToNewsletterWrapper,
        syncGuestCartToServer,
        createTicket: createTicketWrapper,
        getUserTickets: getUserTicketsWrapper,
        getTicketById: getTicketByIdWrapper,
        getTicketUpdates: getTicketUpdatesWrapper,
        replyToTicket: replyToTicketWrapper,
        updateTicket, // Kept for backward compatibility
        deleteTicket: deleteTicketWrapper,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
